/**
 * QuestionDetail Component
 * Visar en enskild fråga med radioknappar för svar (1-6)
 * Efter svar visas nästa fråga i samma tema automatiskt
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import lifeThemeAPI from '../../services/lifeThemeAPI';
import { trackEvent } from '../../utils/analytics';

const QuestionDetail = () => {
  const { themeId, questionId } = useParams();
  const navigate = useNavigate();
  
  const [answerScale, setAnswerScale] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [themeProgress, setThemeProgress] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [themeId, questionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Ladda svarsskala, tema-progress och frågor parallellt
      const [scaleData, progressData, questionsData] = await Promise.all([
        lifeThemeAPI.getAnswerScale(),
        lifeThemeAPI.getThemeProgress(themeId),
        lifeThemeAPI.getThemeQuestions(themeId, true)
      ]);

      console.log('📊 Loaded data:', { 
        scaleCount: scaleData?.length, 
        progressData, 
        questionsCount: questionsData?.length,
        questions: questionsData 
      });

      setAnswerScale(scaleData);
      setThemeProgress(progressData);

      // Kontrollera om det finns några frågor
      if (!questionsData || questionsData.length === 0) {
        setError('Det finns inga frågor för detta livstema än. Kontakta support.');
        return;
      }

      // Hitta aktuell fråga
      const question = questionsData.find(q => q.id === parseInt(questionId));
      setCurrentQuestion(question);

      if (!question) {
        console.warn('⚠️ Question not found:', { questionId, availableIds: questionsData.map(q => q.id) });
        setError('Frågan kunde inte hittas');
      } else {
        // Track question view (authenticated)
        trackEvent('Life Theme Question View Authenticated', {
          theme_id: parseInt(themeId),
          question_id: parseInt(questionId),
          theme_name: progressData?.theme_name,
          question_order: question.question_order,
          location: 'question-detail',
          flow_type: 'authenticated_questionnaire'
        });
      }
    } catch (err) {
      console.error('❌ Error loading question:', err);
      console.error('Error details:', { 
        message: err.message, 
        response: err.response?.data,
        status: err.response?.status 
      });
      setError(`Kunde inte ladda frågan: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSubmit = async () => {
    if (selectedAnswer === null) {
      return;
    }

    try {
      setSubmitting(true);
      
      // Track answer submission (authenticated)
      trackEvent('Life Theme Question Answer Authenticated', {
        theme_id: parseInt(themeId),
        question_id: parseInt(questionId),
        answer_value: selectedAnswer,
        theme_name: themeProgress?.theme_name,
        location: 'question-detail',
        flow_type: 'authenticated_questionnaire'
      });
      
      // Spara svaret
      await lifeThemeAPI.createOrUpdateAnswer(parseInt(questionId), selectedAnswer);

      // Hämta uppdaterad progress
      const updatedProgress = await lifeThemeAPI.getThemeProgress(themeId);
      
      // Kolla om det finns fler frågor i temat
      if (updatedProgress.next_question) {
        // Navigera till nästa fråga
        navigate(`/client/life-themes/${themeId}/question/${updatedProgress.next_question.id}`);
      } else {
        // Alla frågor besvarade - gå till sammanfattning
        trackEvent('Life Theme Complete Authenticated', {
          theme_id: parseInt(themeId),
          theme_name: themeProgress?.theme_name,
          total_questions: updatedProgress.total_questions,
          location: 'question-detail',
          flow_type: 'authenticated_questionnaire'
        });
        navigate(`/client/life-themes/${themeId}/summary`);
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Kunde inte spara svaret. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !currentQuestion) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Frågan kunde inte laddas'}</p>
          <button
            onClick={() => navigate('/client/life-themes')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tillbaka till översikten
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/client')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Tillbaka till översikten
      </button>

      {/* Progress Bar */}
      {themeProgress && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">
              {themeProgress.theme_name}
            </h3>
            <span className="text-sm text-gray-600">
              {themeProgress.answered_questions} / {themeProgress.total_questions} frågor
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-brand-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${themeProgress.progress_percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Question Card */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-sm">
        

        {/* Question Text */}
        <h2 className="text-2xl font-bold text-gray-900 mb-8">
          {currentQuestion.question_text}
        </h2>

        {/* Answer Options */}
        <div className="space-y-3">
          {answerScale.map((scale) => (
            <button
              key={scale.value}
              onClick={() => setSelectedAnswer(scale.value)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedAnswer === scale.value
                  ? 'border-brand-primary bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Radio Button */}
                <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedAnswer === scale.value
                    ? 'border-brand-primary bg-brand-primary'
                    : 'border-gray-300'
                }`}>
                  {selectedAnswer === scale.value && (
                    <div className="w-3 h-3 bg-white rounded-full" />
                  )}
                </div>

                {/* Label */}
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-bold ${
                      selectedAnswer === scale.value ? 'text-brand-primary' : 'text-gray-500'
                    }`}>
                      {scale.value}
                    </span>
                    <span className={`text-base font-medium ${
                      selectedAnswer === scale.value ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                      {scale.label}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={handleAnswerSubmit}
            disabled={selectedAnswer === null || submitting}
            className={`flex-1 py-4 rounded-lg font-semibold text-lg transition-all duration-200 ${
              selectedAnswer === null || submitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary text-white hover:bg-brand-primary-dark shadow-md hover:shadow-lg'
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Sparar...
              </span>
            ) : (
              'Nästa fråga'
            )}
          </button>
        </div>

        {/* Helper Text */}
        <p className="text-sm text-gray-500 text-center mt-4">
          Välj det alternativ som bäst beskriver hur du känner
        </p>
      </div>
    </div>
  );
};

export default QuestionDetail;
